import { pluralise } from '@/lib/format';

/**
 * What a DEAL, QUOTE or INVOICE's tasks and notes cost when the record itself is
 * deleted — the one copy of that sentence.
 *
 * They cascade away with it and the API warns about neither — no 409, no count —
 * so the confirmation is the only place a reader will ever see the number.
 *
 * ══ 🔴 WHY THIS IS A MODULE AND NOT THREE FUNCTIONS ══
 *
 * Extracted 2026-09-19, owner's decision, **after the three copies were measured
 * to have drifted three ways**: `DealDetailPage` counted system notes into its
 * verb agreement and the other two did not, and only `InvoiceDetailPage` said
 * that nothing could be restored. Nothing failed, nothing typechecked
 * differently, and the drift was found by reading the three side by side.
 *
 * ⛔ **Do not reintroduce a local copy on any page.** Two copies agree the day
 * they are written and drift on the first correction, and what drifts here is an
 * ATTRIBUTION on the screen where somebody is about to destroy the thing being
 * attributed.
 *
 * ⛔ **`companyNotesConsequence` in `company-delete.ts` is NOT this function and
 * must not be folded in.** A company's tasks are `ON DELETE RESTRICT` — they
 * REFUSE the delete rather than being destroyed by it — so that sentence must
 * never name tasks at all. Merging the two is how somebody later "harmonises"
 * that away and promises to destroy the one thing that blocks the operation.
 *
 * ══ 🔴 BOTH HUMAN COUNTS ARE FLOORS. ONLY THE SYSTEM ONE IS EXACT ══
 *
 *   - `/notes/summary` applies `visibilityClause`, so without the audit grant
 *     `count` is the reader's OWN notes and nobody else's.
 *   - `/tasks` applies its own — `(t.assigned_to_id = :actorId OR
 *     t.created_by_id = :actorId)`, **measured 2026-09-19** — so `taskTotal` is
 *     a floor too. This is easy to miss: the notes scoping is documented at
 *     length and the task scoping is not.
 *   - `systemNoteTotal` alone is EXACT. `visibilityClause`'s second arm is
 *     `OR n.is_system = 1`, so every reader who may read notes at all sees every
 *     system note, and the figure is identical for everybody.
 *
 * **The cascade has no such clause and destroys all of it.**
 *
 * Two rules follow, both taken from `company-delete.ts`, which had them first:
 *
 *   1. **ZERO IS NOT SILENCE.** None-that-you-can-see is worded identically to
 *      unknown. ⛔ Do not restore a "No tasks or notes" arm — that is a clean
 *      bill of health handed to a reader who simply cannot see a colleague's
 *      forty, immediately before destroying them.
 *   2. **A HUMAN COUNT IS ONLY EVER "AT LEAST"**, and the sentence says out loud
 *      that colleagues' are not in the figure.
 *
 * ⛔ **NEVER DERIVE THE SYSTEM FIGURE BY SUBTRACTION.** The counts run through
 * different rules — one narrowed by `HUMAN_NOTES_ONLY`, one not — so the
 * arithmetic crosses two populations and is wrong by however many notes a
 * colleague happens to have written, with nothing failing.
 *
 * ⚠️ **An exact total would need a count that ignores visibility, and no
 * endpoint offers one.** `countTaskAndNoteReferences` computes it server-side in
 * a single round trip and the service destructures `const { tasks } = …` and
 * throws the notes figure away. **Exposing it is a backend decision, not a
 * consistency fix.** ⛔ Do not synthesise one by paging `/notes` or `/tasks` —
 * both are scoped the same way, so it would produce the same floor at more
 * expense.
 *
 * @param taskTotal tasks the VIEWER CAN SEE, or `null` when unread.
 * @param noteTotal human notes the VIEWER CAN SEE, or `null` when unread.
 * @param systemNoteTotal notes SEED EMERGE wrote itself — exact. **Pass `0` when
 *   `systemCount` is absent**, folded with `typeof s === 'number' ? s : 0` and
 *   never `?? 0`: a real `0` and an absent key both mean "say nothing about
 *   automatic notes", and only the absent one is a guess.
 */
export function attachmentsSentence(
  taskTotal: number | null,
  noteTotal: number | null,
  systemNoteTotal: number,
): string {
  const systemClause =
    systemNoteTotal > 0
      ? `the ${pluralise(systemNoteTotal, 'note')} SEED EMERGE wrote`
      : null;
  const systemVerb = systemNoteTotal === 1 ? 'does' : 'do';

  const parts: string[] = [];
  if (taskTotal !== null && taskTotal > 0) parts.push(pluralise(taskTotal, 'task'));
  if (noteTotal !== null && noteTotal > 0) parts.push(pluralise(noteTotal, 'note'));

  // UNKNOWN and NONE-YOU-CAN-SEE take the SAME arm — rule 1. A `> 0` guard that
  // fell through to "no tasks or notes" is what this replaced.
  if (taskTotal === null || noteTotal === null || parts.length === 0) {
    return systemClause === null
      ? `Any tasks and notes on it go too, colleagues' included. None of it can be restored.`
      : `Any tasks and notes on it go too, colleagues' included — and so ${systemVerb} ${systemClause}. None of it can be restored.`;
  }

  const human = `At least ${parts.join(' and ')} go too — colleagues' are not counted here`;
  return systemClause === null
    ? `${human}. None of it can be restored.`
    : `${human}, and so ${systemVerb} ${systemClause}. None of it can be restored.`;
}
